import {
  enumUnit,
  InventoryTagStatus,
  InventoryTransactionType,
  TagType,
  Unit,
} from '@rematter/frontend-lib-api-client';

import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export type TagAllocationRecord = {
  id: string;
  tagId: string;
  movementId: string;
  quantity: number;
  quantityUnit: Unit;
  occurredAt: string;
  itemName: string;
  originTransactionType: InventoryTransactionType;
  originTransactionItemId: string;
};

export type TagQuantitySummaryRecord = {
  tagId: string;
  inQuantity: number;
  inQuantityUnit: Unit;
  outQuantity: number;
  outQuantityUnit: Unit;
  netQuantity: number;
  netQuantityUnit: Unit;
};

export type TagDetailRecord = {
  id: string;
  tagNumber: string;
  tagType: TagType;
  generatedAt: string;
  status: InventoryTagStatus;
  grossQuantity: number;
  tare: number;
  netQuantity: number;
  itemId: string;
  itemName: string;
  itemSku: string | null;
  facilityId: string;
  lotId?: string;
  lotNumber?: string;
  isRevised: boolean;
  allocations: TagAllocationRecord[];
  allocationsTotalCount: number;
  quantitySummary: TagQuantitySummaryRecord | null;
  createdAt: string;
  updatedAt: string;
};

export const inventoryTagDetailQueryAtom = createQueryAtomFactory(
  'inventory.inventoryTagDetail',
  (params: {
    id: string;
    allocationsLimit?: number;
    allocationsOffset?: number;
  }) => ({
    inventoryTag: {
      __args: {
        id: params.id,
      },
      id: true,
      tagNumber: true,
      tagType: true,
      generatedAt: true,
      status: true,
      grossQuantity: true,
      tare: true,
      netQuantity: true,
      item: {
        id: true,
        name: true,
        skuNumber: true,
      },
      facilityId: true,
      lotId: true,
      lot: {
        id: true,
        lotNumber: true,
      },
      isRevised: true,
      quantitySummary: {
        tagId: true,
        inQuantity: {
          value: true,
          unit: true,
        },
        outQuantity: {
          value: true,
          unit: true,
        },
        netQuantity: {
          value: true,
          unit: true,
        },
      },
      allocations: {
        __args: {
          pagination: {
            limit: params.allocationsLimit ?? 25,
            offset: params.allocationsOffset ?? 0,
          },
        },
        page: {
          total: true,
        },
        entries: {
          id: true,
          tagId: true,
          movementId: true,
          quantity: {
            value: true,
            unit: true,
          },
          movement: {
            id: true,
            occurredAt: true,
            item: {
              id: true,
              name: true,
            },
            originTransactionType: true,
            originTransactionItemId: true,
          },
          timestamps: {
            createdAt: true,
          },
        },
      },
      timestamps: {
        createdAt: true,
        updatedAt: true,
      },
    },
  }),
  {
    select: (data): TagDetailRecord | null => {
      const tag = data?.inventoryTag;
      if (!tag) {
        return null;
      }

      const allocations: TagAllocationRecord[] = (
        tag.allocations?.entries ?? []
      ).map((alloc) => ({
        id: alloc.id,
        tagId: alloc.tagId,
        movementId: alloc.movementId,
        quantity: Number(alloc.quantity?.value) || 0,
        quantityUnit: alloc.quantity?.unit || enumUnit.Pounds,
        occurredAt: alloc.movement?.occurredAt || '',
        itemName: alloc.movement?.item?.name || '',
        originTransactionType: alloc.movement?.originTransactionType,
        originTransactionItemId: alloc.movement?.originTransactionItemId || '',
      }));

      const quantitySummary: TagQuantitySummaryRecord | null =
        tag.quantitySummary
          ? {
              tagId: tag.quantitySummary.tagId,
              inQuantity: Number(tag.quantitySummary.inQuantity?.value) || 0,
              inQuantityUnit:
                tag.quantitySummary.inQuantity?.unit || enumUnit.Pounds,
              outQuantity: Number(tag.quantitySummary.outQuantity?.value) || 0,
              outQuantityUnit:
                tag.quantitySummary.outQuantity?.unit || enumUnit.Pounds,
              netQuantity: Number(tag.quantitySummary.netQuantity?.value) || 0,
              netQuantityUnit:
                tag.quantitySummary.netQuantity?.unit || enumUnit.Pounds,
            }
          : null;

      return {
        id: tag.id,
        tagNumber: tag.tagNumber,
        tagType: tag.tagType,
        generatedAt: tag.generatedAt,
        status: tag.status,
        grossQuantity: Number(tag.grossQuantity),
        tare: Number(tag.tare),
        netQuantity: Number(tag.netQuantity),
        itemId: tag.item?.id || '',
        itemName: tag.item?.name || '',
        itemSku: tag.item?.skuNumber || null,
        facilityId: tag.facilityId,
        lotId: tag.lotId || undefined,
        lotNumber: tag.lot?.lotNumber || undefined,
        isRevised: tag.isRevised,
        allocations,
        allocationsTotalCount: tag.allocations?.page?.total || 0,
        quantitySummary,
        createdAt: tag.timestamps?.createdAt || '',
        updatedAt: tag.timestamps?.updatedAt || '',
      };
    },
  },
);
